import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Artist, PortfolioItem } from '@/types/models'
import { CountryFlag } from '@/lib/countryFlags'
import { useUserData } from '@/hooks/useUserData'
import { Send } from 'lucide-react'
import { useAnalytics } from '@/hooks/useAnalytics'
import { RatingSummary } from '@/components/reviews/RatingSummary'
import { checkArtistHasBookingSystem } from '@/lib/tattooData'
import { useImageLoadAndVisible } from '@/hooks/useImageLoadAndVisible'

interface ArtistsListProps {
  artists: Artist[]
  showCountryFlags?: boolean
  isLoading?: boolean
}

// Separate component for portfolio image card with image loading detection
function PortfolioImageCard({ item, isHidden }: { item: PortfolioItem; isHidden: boolean }) {
  const [imageReady, setImageReady] = useState(false)
  
  const { imgRef, handleImageLoad } = useImageLoadAndVisible({
    onImageLoadAndVisible: () => {
      setImageReady(true)
    },
    preloaded: false
  })

  return (
    <div className={`relative aspect-square min-h-0 w-full ${isHidden ? 'hidden md:block' : ''}`}>
      {/* Skeleton stays visible until image is fully visible */}
      <div className={`absolute inset-0 w-full h-full bg-muted rounded-xl transition-opacity duration-500 ${
        imageReady ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-pulse'
      }`} />
      
      <img
        ref={imgRef}
        src={item.compressedImageUrl || item.imageUrl}
        alt={item.caption || 'Portfolio item'}
        className={`w-full h-full object-cover rounded-xl transition-opacity duration-500 ease-out ${
          imageReady ? 'opacity-100' : 'opacity-0'
        }`}
        loading="lazy"
        onLoad={handleImageLoad}
        onError={(e) => {
          const target = e.currentTarget as HTMLImageElement
          target.style.opacity = '0'
        }}
      />
    </div>
  )
}

const avatarSizeClasses = 'w-16 h-16 md:w-20 md:h-20'

// Separate component for avatar with image loading detection
function AvatarImage({ 
  artist, 
  hasBookingSystem, 
  isTextContentReady,
}: { 
  artist: Artist
  hasBookingSystem: boolean
  isTextContentReady: boolean
}) {
  const { t } = useTranslation()
  const [imageReady, setImageReady] = useState(false)
  
  const { imgRef, handleImageLoad } = useImageLoadAndVisible({
    onImageLoadAndVisible: () => {
      setImageReady(true)
    },
    preloaded: false
  })

  const artistDisplayName = artist.displayName || t('common.artistName')

  const hasAvatar = artist.avatarUrl || artist.compressedAvatarUrl

  const borderClasses = `border-4 ${hasBookingSystem ? 'border-amber-400' : 'border-border'}`

  const innerRadiusClass = 'rounded-2xl'

  const dimensionClasses = avatarSizeClasses

  const avatarInner = (
    <>
      {/* Skeleton background */}
      <div 
        className={`relative ${dimensionClasses} ${innerRadiusClass} flex items-center justify-center shadow-md transition-opacity duration-500 ${
          imageReady && hasAvatar ? 'opacity-0 pointer-events-none' : 'opacity-100'
        } ${
          imageReady && hasAvatar ? 'bg-transparent' : 'bg-muted animate-pulse'
        } ${borderClasses}`}
      >
        {isTextContentReady && !hasAvatar ? (
          <span className="text-lg font-medium">
            {artist.displayName?.[0] || '?'}
          </span>
        ) : null}
      </div>
      
      {/* Avatar image */}
      {hasAvatar && (
        <img 
          ref={imgRef}
          src={artist.compressedAvatarUrl || artist.avatarUrl} 
          alt={artistDisplayName}
          className={`absolute inset-0 ${dimensionClasses} ${innerRadiusClass} object-cover shadow-md transition-opacity duration-500 ${
            imageReady ? 'opacity-100' : 'opacity-0'
          } ${borderClasses}`}
          onLoad={handleImageLoad}
          onError={(e) => {
            const target = e.currentTarget as HTMLImageElement
            target.style.opacity = '0'
          }}
        />
      )}
    </>
  )

  return (
    <div className="relative flex-shrink-0">
      {avatarInner}
    </div>
  )
}

export function ArtistsList({ artists: inputArtists, showCountryFlags = false, isLoading = false }: ArtistsListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isOwnArtistPage, isAdminModeActive } = useUserData()
  const { trackEvent } = useAnalytics()
  const [portfolioItems, setPortfolioItems] = useState<Record<string, PortfolioItem[]>>({})
  const [loading, setLoading] = useState(false)
  const [bookingStatusByArtist, setBookingStatusByArtist] = useState<Record<string, boolean>>({})

  const matcherSessionId = searchParams.get('matcherSessionId')
  const matcherUserId = searchParams.get('matcherUserId')
  const matcherSelectedCity = searchParams.get('matcherSelectedCity') || ''
  const matcherIntentMode = searchParams.get('matcherIntentMode') === 'specific' ? 'specific' : 'exploring'
  const matcherIdeaText = searchParams.get('matcherIdeaText') || ''
  const sourceParam = searchParams.get('source') || 'artists_list'

  // Log artist query data
  useEffect(() => {
    if (inputArtists.length > 0) {
      console.group('🎨 [ArtistsList] Artist Query Results')
      console.log(`Total artists: ${inputArtists.length}`)
      console.table(inputArtists.map(a => ({
        id: a.id,
        name: a.displayName,
        isAvailable: a.isAvailable,
        city: a.location?.city,
        followers: a.followers?.length || 0
      })))
      console.log('Full artist data:', inputArtists)
      console.groupEnd()
    } else {
      console.log('🎨 [ArtistsList] No artists received')
    }
  }, [inputArtists])

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (inputArtists.length === 0) {
      setPortfolioItems({})
      setLoading(false)
      return
    }

    const fetchPortfolios = async () => {
      try {
        setLoading(true)
        setPortfolioItems({})

        // Fetch portfolio items
        const portfolioSnapshot = await getDocs(collection(db, "portfolioItems"))
        
        const allPortfolioItems = portfolioSnapshot.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            artistId: data.artistId,
            type: data.type || 'image',
            storagePath: data.storagePath || '',
            imageUrl: data.imageUrl,
            compressedImageUrl: data.compressedImageUrl,
            width: data.width || 0,
            height: data.height || 0,
            styleTags: data.styles || data.styleTags || [],
            caption: data.caption || '',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            saves: data.saves || [],
            timedSaves: data.timedSaves || []
          } as PortfolioItem & { saves: string[] }
        })
        
        // Group portfolio items by artist and sort by saves count, take top 4 (desktop shows 4)
        const portfolioByArtist: Record<string, PortfolioItem[]> = {}
        
        inputArtists.forEach(artist => {
          const artistPortfolio = allPortfolioItems
            .filter(item => item.artistId === artist.id)
            .sort((a, b) => {
              const aSaves = (a.timedSaves?.length || a.saves?.length || 0)
              const bSaves = (b.timedSaves?.length || b.saves?.length || 0)
              return bSaves - aSaves
            })
            .slice(0, 4)
          
          portfolioByArtist[artist.id] = artistPortfolio
        })
        
        setPortfolioItems(portfolioByArtist)
      } catch (err) {
        console.error("Error fetching portfolio data:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchPortfolios()
  }, [inputArtists, isLoading])

  useEffect(() => {
    if (isLoading) return

    const artistsToCheck = inputArtists.filter(artist => bookingStatusByArtist[artist.id] === undefined)

    if (artistsToCheck.length === 0) return

    let isCancelled = false

    const checkBookingSystems = async () => {
      try {
        const results = await Promise.all(
          artistsToCheck.map(async (artist) => {
            const hasBooking = await checkArtistHasBookingSystem(artist.id)
            return [artist.id, hasBooking] as const
          })
        )

        if (isCancelled) return

        setBookingStatusByArtist(prev => {
          const next = { ...prev }
          results.forEach(([artistId, hasBooking]) => {
            next[artistId] = hasBooking
          })
          return next
        })
      } catch (error) {
        console.error('[ArtistsList] Error checking booking system status:', error)
      }
    }

    checkBookingSystems()

    return () => {
      isCancelled = true
    }
  }, [inputArtists, isLoading, bookingStatusByArtist])

  const handleArtistClick = (artistId: string, position?: number) => {
    trackEvent('artist_card_clicked', {
      artist_id: artistId,
      position,
      source: 'artists_list'
    })
    navigate(`/artist/${artistId}`)
  }

  const handleMessageArtist = (artistId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    const chatParams = new URLSearchParams({
      artistId,
      source: sourceParam,
    })

    if (matcherSessionId) {
      chatParams.set('matcherSessionId', matcherSessionId)
      chatParams.set('matcherUserId', matcherUserId || '')
      chatParams.set('matcherSelectedCity', matcherSelectedCity)
      chatParams.set('matcherIntentMode', matcherIntentMode)

      if (matcherIdeaText.trim()) {
        chatParams.set('matcherIdeaText', matcherIdeaText)
      }
    }

    trackEvent('artist_message_clicked', {
      artist_id: artistId,
      source: sourceParam,
    })
    navigate(`/chats/new?${chatParams.toString()}`)
  }

  // Loading: skeleton cards; loaded empty: same muted copy pattern as Feed Grid empty state
  if (inputArtists.length === 0) {
    if (isLoading) {
      return (
        <div className="flex w-full min-w-0 flex-col gap-1 space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="relative w-full min-w-0 overflow-hidden bg-card rounded-2xl p-2 shadow-sm border border-border/70">
              <div className="relative z-10 flex items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-muted-foreground/20 animate-pulse border-4 border-border" />
                    <div className="absolute -bottom-1 -right-1 z-20 w-4 h-4 rounded-full bg-muted-foreground/20 animate-pulse border border-border" />
                  </div>
                  <div className="flex-1 min-w-0" />
                </div>
                <div className="flex-shrink-0">
                  <button
                    disabled
                    className="m-1 h-14 w-14 rounded-2xl bg-muted text-muted-foreground cursor-not-allowed flex items-center justify-center shadow-sm"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="relative z-10 mt-2 grid w-full min-w-0 grid-cols-3 md:grid-cols-4 gap-1.5">
                {[...Array(4)].map((_, j) => (
                  <div
                    key={j}
                    className={`aspect-square min-h-0 w-full bg-muted/60 rounded-xl animate-pulse ${j >= 3 ? 'hidden md:block' : ''}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className="py-3 text-left">
        <p className="text-base font-medium text-muted-foreground leading-snug">
          {t('artists.emptyList')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-1 space-y-2">
      {inputArtists.map((artist, index) => {
        const hideMessageCTA = isOwnArtistPage(artist.id) || isAdminModeActive
        const hasBookingSystem = bookingStatusByArtist[artist.id] === true
        const artistPortfolio = portfolioItems[artist.id]
        const isPortfolioLoaded = artistPortfolio !== undefined
        const showPortfolioSkeleton = !isPortfolioLoaded && (loading || isLoading)
        
        // Check if text content is ready
        const isNameReady = !!artist.displayName
        const isBookingStatusReady = bookingStatusByArtist[artist.id] !== undefined
        const isTextContentReady = isNameReady && isBookingStatusReady
        
        return (
              <div
                key={artist.id}
                className="relative w-full min-w-0 overflow-hidden bg-card rounded-2xl p-2 cursor-pointer transition-colors shadow-sm border border-border/70 hover:bg-card/95"
                onClick={() => handleArtistClick(artist.id, index + 1)}
              >
                {/* Header row */}
                <div className="relative z-10 flex items-center gap-3">
                  {/* Left side: Profile picture and text */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="relative">
                      <AvatarImage 
                        artist={artist}
                        hasBookingSystem={hasBookingSystem}
                        isTextContentReady={isTextContentReady}
                      />
                      {/* Country flag - only shown when city filter is not specified */}
                      {showCountryFlags && (
                        <div className={`absolute -bottom-1 -right-1 z-20 transition-opacity duration-500 ${
                          isTextContentReady ? 'opacity-100' : 'opacity-0'
                        }`}>
                          <CountryFlag country={artist.location?.country} size="lg" />
                        </div>
                      )}
                    </div>
                    
                    {/* Card content - text fades in once ready */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      {isTextContentReady && (
                        <div className="animate-in fade-in duration-500">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-lg leading-tight truncate">
                              {artist.displayName || t('common.artistName')}
                            </h3>
                          </div>
                          <RatingSummary artistId={artist.id} className="mt-1" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side: Message (same flow as ArtistPage) */}
                  {!hideMessageCTA && (
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => handleMessageArtist(artist.id, e)}
                        disabled={!isTextContentReady}
                        className={`h-14 w-14 m-1 rounded-2xl shadow-sm flex items-center justify-center transition-all duration-500 ${
                          isTextContentReady
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : 'bg-muted text-muted-foreground cursor-not-allowed'
                        }`}
                        aria-label="Message artist"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Portfolio images grid */}
                <div className="relative z-10 mt-2 grid w-full min-w-0 grid-cols-3 md:grid-cols-4 gap-1.5">
                  {showPortfolioSkeleton ? (
                    [...Array(4)].map((_, index) => (
                      <div
                        key={index}
                        className={`aspect-square min-h-0 w-full bg-muted/60 rounded-xl animate-pulse ${
                          index >= 3 ? 'hidden md:block' : ''
                        }`}
                      />
                    ))
                  ) : artistPortfolio && artistPortfolio.length > 0 ? (
                    artistPortfolio.map((portfolioItem, itemIndex) => (
                      <PortfolioImageCard
                        key={portfolioItem.id}
                        item={portfolioItem}
                        isHidden={itemIndex >= 3}
                      />
                    ))
                  ) : (
                    // Keep showing skeleton if no portfolio items
                    [...Array(4)].map((_, index) => (
                      <div
                        key={index}
                        className={`aspect-square min-h-0 w-full bg-muted/60 rounded-xl animate-pulse ${
                          index >= 3 ? 'hidden md:block' : ''
                        }`}
                      />
                    ))
                  )}
                </div>
              </div>
        )
      })}
    </div>
  )
}
